import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, relativeTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PhotoUpload from "@/components/PhotoUpload";
import { toast } from "sonner";
import { ListTodo, SprayCan, ListChecks, Flame, Clock, MapPin, User, CheckCircle2 } from "lucide-react";

const GROUPS = [
  { key: "overdue", label: "Overdue", tone: "text-red-600" },
  { key: "today", label: "Due today", tone: "text-brand-dark" },
  { key: "upcoming", label: "Upcoming", tone: "text-slate-600" },
  { key: "nodue", label: "No due date", tone: "text-slate-500" },
];
const FILTERS = [{ k: "all", l: "All" }, { k: "cleaning", l: "Cleaning" }, { k: "prep", l: "Prep" }];

export default function MyTasks() {
  const { has } = useAuth();
  const [data, setData] = useState({ linked: null, items: [] });
  const [filter, setFilter] = useState("all");
  const [photoInst, setPhotoInst] = useState(null);

  const load = async () => setData((await api.get("/me/tasks")).data);
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("cc-site-changed", h);
    window.addEventListener("cc-tasks-changed", h);
    return () => {
      window.removeEventListener("cc-site-changed", h);
      window.removeEventListener("cc-tasks-changed", h);
    };
  }, []);

  const setupProfile = async () => {
    try { await api.post("/me/person"); toast.success("Staff profile ready"); load(); }
    catch (e) { toast.error(e?.normalized?.message || "Couldn't set up your staff profile"); }
  };

  const complete = async (item) => {
    try {
      if (item.kind === "prep") await api.post(`/prep/${item.id}/complete`);
      else if (item.kind === "cleaning_instance") {
        if (item.photo_required) { setPhotoInst(item); return; }
        await api.post(`/cleaning/instances/${item.id}/complete`, { photo_file_id: null });
      }
      toast.success("Task completed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to complete"); }
  };

  if (data.linked === null) return null;
  if (data.linked === false) {
    return (
      <div>
        <PageHeader title="My Tasks" subtitle="Your assigned cleaning and prep work." testid="my-tasks-header" />
        {data.can_self_setup ? (
          <div className="text-center py-12" data-testid="my-tasks-self-setup">
            <ListTodo className="mx-auto text-slate-300" size={40} />
            <h3 className="mt-3 text-base font-semibold text-slate-700">Set up your staff profile</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">Your account isn't linked to a staff profile yet. Create one to receive and complete tasks assigned to you.</p>
            <Button className="mt-4 bg-brand hover:bg-brand-hover" onClick={setupProfile} data-testid="self-setup-person-btn">Set up my staff profile</Button>
          </div>
        ) : (
          <EmptyState icon={ListTodo} title="No workforce profile is linked to your account."
            message="Ask a manager to link your login to your staff profile to see tasks assigned to you." />
        )}
      </div>
    );
  }

  const items = data.items.filter((i) => filter === "all" || i.module === filter);
  const sortKey = (i) => [i.urgent ? 0 : 1, i.due || i.due_time || "~", i.title];
  const inGroup = (g) => items.filter((i) => i.group === g).sort((a, b) => (sortKey(a) < sortKey(b) ? -1 : 1));

  return (
    <div>
      <PageHeader title="My Tasks" subtitle="Your assigned cleaning and prep work." testid="my-tasks-header" />

      <div className="flex gap-2 mb-5" data-testid="my-tasks-filters">
        {FILTERS.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`my-tasks-filter-${f.k}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === f.k ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {f.l}
          </button>
        ))}
      </div>

      {items.length === 0 ? <EmptyState icon={CheckCircle2} title="Nothing assigned to you." message="Tasks assigned to you will show up here." /> :
        <div className="space-y-6">
          {GROUPS.map((g) => {
            const rows = inGroup(g.key);
            if (rows.length === 0) return null;
            return (
              <div key={g.key} data-testid={`my-tasks-group-${g.key}`}>
                <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${g.tone}`}>{g.label} ({rows.length})</div>
                <div className="space-y-2">
                  {rows.map((i) => <TaskCard key={`${i.kind}-${i.id}`} item={i} canComplete={i.completable} onComplete={() => complete(i)} />)}
                </div>
              </div>
            );
          })}
        </div>}

      {photoInst && <PhotoCompleteModal item={photoInst} onClose={() => setPhotoInst(null)} onDone={() => { setPhotoInst(null); load(); }} />}
    </div>
  );
}

function TaskCard({ item, canComplete, onComplete }) {
  const ModIcon = item.module === "cleaning" ? SprayCan : ListChecks;
  const isDone = item.status === "completed";
  return (
    <div className={`cc-card p-4 ${item.urgent ? "border-red-200" : ""}`} data-testid={`my-task-${item.kind}-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${item.module === "cleaning" ? "bg-brand-light text-brand-dark" : "bg-amber-100 text-amber-700"}`}><ModIcon size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 break-words">{item.title}</span>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-500" data-testid={`my-task-module-${item.kind}-${item.id}`}>{item.module}</span>
            {item.urgent && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-600 px-2 py-0.5 rounded-full flex items-center gap-1"><Flame size={11} /> Urgent</span>}
            {item.status === "recurring" && <span className="text-[10px] font-medium bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full capitalize">{item.frequency || "recurring"}</span>}
          </div>
          {item.details && <div className="text-xs text-slate-500 mt-0.5 break-words">{item.details}</div>}
          <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
            {item.site && <span className="flex items-center gap-0.5"><MapPin size={11} /> {item.site}</span>}
            <span className="flex items-center gap-0.5"><Clock size={11} /> {item.due ? relativeTime(item.due) : (item.due_time || "no due time")}</span>
            {item.priority === "high" && <span className="text-amber-600 font-medium">high priority</span>}
            {item.assignee && <span className="flex items-center gap-0.5"><User size={11} /> {item.assignee}</span>}
          </div>
        </div>
        {isDone ? <span className="text-[11px] text-brand-dark font-semibold shrink-0">Done</span> :
          canComplete && <Button size="sm" className="bg-brand hover:bg-brand-hover rounded-lg shrink-0" data-testid={`my-task-complete-${item.kind}-${item.id}`} onClick={onComplete}>Complete</Button>}
      </div>
    </div>
  );
}

function PhotoCompleteModal({ item, onClose, onDone }) {
  const [photo, setPhoto] = useState(null);
  const submit = async () => {
    try { await api.post(`/cleaning/instances/${item.id}/complete`, { photo_file_id: photo }); toast.success("Task completed"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="my-task-photo-modal">
        <DialogHeader><DialogTitle>{item.title}</DialogTitle></DialogHeader>
        <PhotoUpload value={photo} onChange={setPhoto} required label="Photo proof required" testid="my-task-photo" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={!photo} data-testid="my-task-confirm-photo" className="bg-brand hover:bg-brand-hover">Mark complete</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
